import { useCallback, useEffect, useState } from "react";
import { galleryItems } from "@/mocks/gallery";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

export default function Gallery() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const prev = useCallback(
    () => setOpenIndex((i) => (i === null ? i : (i - 1 + galleryItems.length) % galleryItems.length)),
    []
  );
  const next = useCallback(
    () => setOpenIndex((i) => (i === null ? i : (i + 1) % galleryItems.length)),
    []
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft") prev();
      if (event.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, close, prev, next]);

  const active = openIndex === null ? null : galleryItems[openIndex];

  return (
    <section id="galeri" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto w-full max-w-7xl px-5 md:px-6">
        <Reveal>
          <SectionHeading
            eyebrow="Galeri"
            title="Fatma Angay Güzellik Salonu’ndan"
            description="Salonumuzdan, uygulama ortamımızdan ve güncel kampanyalarımızdan gerçek görseller."
          />
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-12 columns-2 gap-4 md:gap-5 lg:columns-3">
            {galleryItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setOpenIndex(index)}
                className="group mb-4 block w-full cursor-pointer break-inside-avoid overflow-hidden rounded-xl border border-background-300/70 bg-primary-950 text-left transition-transform duration-300 hover:-translate-y-1 md:mb-5"
              >
                <div className="relative">
                  <img
                    src={item.src}
                    alt={item.alt}
                    title={`${item.title} - Fatma Angay Güzellik Salonu`}
                    className="h-auto w-full"
                  />
                  <span className="absolute inset-0 flex items-end bg-gradient-to-t from-primary-950/85 via-primary-950/10 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="flex items-center gap-2 font-label text-xs font-medium tracking-wide text-background-50">
                      <span className="flex h-5 w-5 items-center justify-center">
                        <i className="ri-zoom-in-line text-base text-accent-400" aria-hidden="true" />
                      </span>
                      {item.title}
                    </span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Reveal>
      </div>

      {active ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/90 p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={active.title}
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Görseli kapat"
            className="absolute right-4 top-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-accent-500/50 text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950 md:right-8 md:top-8"
          >
            <i className="ri-close-line text-2xl" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              prev();
            }}
            aria-label="Önceki görsel"
            className="absolute left-2 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-accent-500/50 text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950 md:left-8"
          >
            <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              next();
            }}
            aria-label="Sonraki görsel"
            className="absolute right-2 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-accent-500/50 text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950 md:right-8"
          >
            <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
          </button>

          <figure
            className="flex max-h-full w-full max-w-3xl flex-col items-center gap-4"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={active.src}
              alt={active.alt}
              title={`${active.title} - Fatma Angay Güzellik Salonu`}
              className="max-h-[74vh] w-auto max-w-full rounded-xl border border-accent-500/40 object-contain"
            />
            <figcaption className="font-label text-xs uppercase tracking-[0.2em] text-accent-300">
              {active.title}
            </figcaption>
          </figure>
        </div>
      ) : null}
    </section>
  );
}